import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Page, Document } from 'react-pdf';
import styled from 'styled-components';
import { Box, Button, ButtonGroup, CircularProgress, Collapse, Fade, Grid, LinearProgress, Tooltip, Typography } from '@material-ui/core';
import { Add, Remove, Warning, ZoomIn } from '@material-ui/icons';
import { createProxyUrl, ErrorContent } from '@sierralabs/core-ui';
import { useElementSize } from '../../common/hooks/useElementSize';
const StyledContainer = styled(Grid) `
  height: 100%;
  position: relative;
  background-color: rgb(68, 68, 68);
  .wrapper {
    flex: 1;
    width: 100%;
    display: flex;
    overflow: auto;
    align-items: center;
    flex-direction: column;
    min-height: 500px;
  }
  .text {
    color: #fff;
  }
  .text-secondary {
    color: rgba(255, 255, 255, 0.7);
  }
  .document {
    height: 0px;
  }
  .page {
    padding-top: 5px;
    &:not(:last-child) {
      padding-bottom: 5px;
    }
    &:last-child {
      padding-bottom: 68px;
    }
  }
  canvas {
    margin-left: auto;
    margin-right: auto;
    box-shadow: ${props => props.theme.shadows[8]};
  }
  .zoom {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    margin-bottom: 16px;
    color: rgb(237, 239, 245);
    .buttons,
    .MuiButtonGroup-root {
      margin-left: auto;
      margin-right: auto;
      background-color: rgba(0, 0, 0, 0.6);
    }
  }
`;
const PDFViewer = props => {
    const { url, storageKey, className, resizeOnce = false } = props;
    const scaleAdjust = 0.1;
    const containerRef = useRef(null);
    const [error, setError] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [pdf, setPdf] = useState();
    const [pagesRendered, setPagesRendered] = useState(0);
    const [scale, setScale] = useState(1);
    const [width, setWidth] = useState();
    const [containerWidthOnce] = useElementSize({
        once: true,
        elementRef: containerRef
    });
    const [containerWidth] = useElementSize({
        once: resizeOnce,
        elementRef: containerRef
    });
    const numPages = useMemo(() => (pdf === null || pdf === void 0 ? void 0 : pdf.numPages) || 0, [pdf]);
    const renderLength = numPages
        ? Math.min(numPages, pagesRendered + 1)
        : pagesRendered + 1;
    const fileUrl = useMemo(() => {
        return !storageKey
            ? url
            : createProxyUrl(storageKey, { key: 'storageKey', path: 'files/proxy' }, undefined, true).toString();
    }, [url, storageKey]);
    useEffect(() => {
        setWidth(containerWidthOnce);
    }, [containerWidthOnce]);
    return (React.createElement(StyledContainer, { container: true, direction: 'column', className: className, innerRef: (ref) => (containerRef.current = ref) },
        React.createElement(Collapse, { in: pagesRendered < numPages },
            React.createElement(LinearProgress, { variant: 'determinate', value: numPages && (pagesRendered / numPages) * 100 })),
        React.createElement("div", { className: 'wrapper', style: pdf ? undefined : { justifyContent: 'center' } },
            React.createElement(Document, { file: fileUrl, error: '', loading: '', className: 'document', onLoadSuccess: pdf => {
                    setIsLoading(false);
                    setPdf(pdf);
                }, onLoadError: (error) => {
                    setIsLoading(false);
                    setError(error);
                } }, Array.from({ length: renderLength }, (_, index) => {
                const renderNextPage = () => {
                    if (pagesRendered < numPages) {
                        console.log('Rendered page', index + 1);
                        setPagesRendered(pagesRendered + 1);
                    }
                };
                return (React.createElement(Page, { key: `page-${index}`, className: 'page', pageNumber: index + 1, scale: scale, width: width && width - 64, onRenderError: renderNextPage, onRenderSuccess: renderNextPage }));
            })),
            isLoading && !error && (React.createElement(Box, { display: 'flex', padding: '20px', alignItems: 'center', flexDirection: 'column' },
                React.createElement(CircularProgress, { color: 'primary' }),
                React.createElement(Box, { mt: 2 },
                    React.createElement(Typography, { variant: 'body1', className: 'text' }, "Loading PDF...")))),
            !isLoading && error && (React.createElement(ErrorContent, { title: 'PDF Error', className: 'text', icon: React.createElement(Warning, { className: 'icon', htmlColor: 'rgba(255, 255, 255, 0.5)' }), description: React.createElement(Typography, { variant: 'body1', className: 'text-secondary' }, error.message || 'Failed to load PDF file.') }))),
        React.createElement(Fade, { in: !!pdf },
            React.createElement("div", { className: 'zoom' },
                React.createElement(ButtonGroup, { color: 'inherit', variant: 'text', className: 'buttons' },
                    React.createElement(Tooltip, { arrow: true, title: 'Zoom Out' },
                        React.createElement(Button, { onClick: () => {
                                const newScale = scale - scaleAdjust;
                                setScale(newScale <= scaleAdjust ? scaleAdjust : newScale);
                            } },
                            React.createElement(Remove, null))),
                    React.createElement(Tooltip, { arrow: true, title: 'Fit to width' },
                        React.createElement(Button, { onClick: () => {
                                setScale(1);
                                setWidth(containerWidth);
                            } },
                            React.createElement(ZoomIn, null))),
                    React.createElement(Tooltip, { arrow: true, title: 'Zoom In' },
                        React.createElement(Button, { onClick: () => setScale(scale + scaleAdjust) },
                            React.createElement(Add, { color: 'inherit' }))))))));
};
export default PDFViewer;
//# sourceMappingURL=PDFViewer.js.map